import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getDistance as geolib_getDistance } from 'geolib';
import { sortBy } from 'lodash';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ILocation, IPOI, IPOICategory, IPOIPATCH, IPOIPOST, POIFilter, TaskApiService } from 'shared';

import { EntityAction } from '../../models/entity-action.model';
import { AppStateService } from './state';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class POIService {
    private readonly tasksApi = inject(TaskApiService);
    private readonly tenantService = inject(TenantService);
    private readonly appState = inject(AppStateService);

    private readonly _pois: BehaviorSubject<Array<IPOI>> = new BehaviorSubject<Array<IPOI>>([]);

    private readonly _poiCategories: BehaviorSubject<Array<IPOICategory>> = new BehaviorSubject<Array<IPOICategory>>(
        []
    );

    // Full list of POIs, including archived ones. Used to resolve POI names/descriptions for
    // entities (issues, tasks, forms, assets) that still reference an archived POI.
    public readonly poisIncludingArchived = this._pois.asObservable();
    // Active (non-archived) POIs. Used for pickers, search and the map, which should not offer archived POIs.
    public readonly pois = this.poisIncludingArchived.pipe(
        map((pois) => pois.filter((poi) => !poi.archived)),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public readonly poiCategories = this._poiCategories.asObservable();

    public constructor() {
        this.appState.loggedInWithTenant.distinctStream
            .pipe(
                takeUntilDestroyed(),
                switchMap((loggedInWithTenant) => {
                    if (loggedInWithTenant) {
                        return combineLatest([this.refreshPOICategories(), this.refreshPOIs()]);
                    } else {
                        this._pois.next([]);
                        this._poiCategories.next([]);

                        return of(null);
                    }
                })
            )
            .subscribe();
    }

    public refreshPOIs(): Observable<Array<IPOI>> {
        return this.tasksApi.getPOIs({ includeArchived: true }).pipe(
            tap((pois) => {
                this._pois.next(pois);
            })
        );
    }

    public searchPOIs(filter: POIFilter): Observable<Array<IPOI>> {
        return this.pois.pipe(
            map((pois) => {
                pois = pois.filter(
                    (poi) => (this.getCategoryForPOISync(poi) || {}).appearInSearch || filter.includeNonSearchables
                );
                pois = pois.filter((poi) => poi.description || filter.includeNoDescription);
                if (filter.floor !== undefined) pois = pois.filter((poi) => poi.location.floor === filter.floor);
                if (filter.latitude !== undefined && filter.longitude !== undefined)
                    pois = sortBy(pois, [
                        // For future reference: can use getPreciseDistance or specify accuracy. performance tradeoff.
                        (poi): number =>
                            geolib_getDistance(poi.location, {
                                longitude: filter.longitude,
                                latitude: filter.latitude,
                            }),
                    ]);
                if (filter.maxResults !== undefined) pois = pois.slice(0, filter.maxResults);
                return pois;
            })
        );
    }

    public getClosestPOI(location: ILocation, includeNoDescription = false): Observable<IPOI> {
        return this.searchPOIs({ ...location, includeNonSearchables: true, includeNoDescription }).pipe(
            map((results) => (results.length ? results[0] : null))
        );
    }

    public createPOI(poi: IPOIPOST): Observable<IPOI> {
        return (
            this.tasksApi
                .postPOI(poi)
                // Add poi locally
                .pipe(tap((p) => this.localAdd(p)))
        );
    }

    public updatePOI(poi: IPOIPATCH): Observable<IPOI> {
        return (
            this.tasksApi
                .patchPOI(poi)
                // Add poi locally
                .pipe(tap((p) => this.localUpdate(p)))
        );
    }

    public archivePOI(poi: IPOI): Observable<IPOI> {
        return (
            this.tasksApi
                .archivePOI(poi.id)
                // Mark poi archived locally: it drops out of active lists (map, pickers) but stays
                // available for name resolution in overviews.
                .pipe(tap((p) => this.localUpdate(p)))
        );
    }

    public deletePOI(poi: IPOI): Observable<void> {
        return (
            this.tasksApi
                .deletePOI(poi)
                // Remove poi locally
                .pipe(tap(() => this.localRemove(poi)))
        );
    }

    public getPOIForId(id: string): Observable<IPOI> {
        return this.poisIncludingArchived.pipe(map((pois) => pois.find((poi) => poi.id === id)));
    }

    public getPOIForIds(ids: Array<string>): Observable<Array<IPOI>> {
        return this.poisIncludingArchived.pipe(map((pois) => pois.filter((poi) => ids.includes(poi.id))));
    }

    public getPOIForIdSync(id: string): IPOI {
        return this._pois.value.find((poi) => poi.id === id);
    }

    public getCategoryForId(categoryId: string): Observable<IPOICategory> {
        return this.poiCategories.pipe(map((categories) => categories.find((c) => c.id === categoryId)));
    }

    public getCategoriesForIds(categoryIds: Array<string>): Observable<Array<IPOICategory>> {
        return this.poiCategories.pipe(map((categories) => categories.filter((c) => categoryIds.includes(c.id))));
    }

    public getCategoryForPOISync(poi: IPOI): IPOICategory {
        return this.getCategoryForIdSync(poi.categoryId);
    }

    public getCategoryForIdSync(categoryId: string): IPOICategory {
        return this._poiCategories.value.find((c) => c.id === categoryId);
    }

    public async updatePOICategories(newCategories: Array<IPOICategory>): Promise<void> {
        const newIds = newCategories.map((newCategory) => newCategory.id);
        this._poiCategories.next([
            ...this._poiCategories.value.filter((category) => !newIds.includes(category.id)),
            ...newCategories,
        ]);
    }

    public localUpdate = async (poi: IPOI): Promise<IPOI> => {
        const pois = (await firstValueFrom(this._pois)).map((p) => (p.id === poi.id ? poi : p));
        this._pois.next(pois);
        return poi;
    };

    public localAdd = async (poi: IPOI): Promise<void> => {
        const pois = await firstValueFrom(this._pois);
        if (pois.find((p) => p.id === poi.id)) return null;
        pois.push(poi);
        this._pois.next(pois);
    };

    public localRemove = async (poi: IPOI): Promise<void> => {
        const pois = await firstValueFrom(this._pois);
        const index = pois.findIndex((p) => p.id === poi.id);
        if (index >= 0) pois.splice(index, 1);
        this._pois.next(pois);
    };

    public buildActions = async (poi: IPOI): Promise<Array<EntityAction>> => {
        const actions: Array<EntityAction> = [];
        if (!poi) return actions;

        // Placeholder for future POI actions

        return actions;
    };

    private refreshPOICategories(): Observable<Array<IPOICategory>> {
        return this.tenantService.config.pipe(
            map((config) => {
                const poiCategories = config.taskApi.poiCategories;
                // Publish poi categories and return them
                this._poiCategories.next(poiCategories);

                return poiCategories;
            })
        );
    }
}
